import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import MethodologyDrawer from './MethodologyDrawer'

type OpenArgs = {
  hazardCode: string
  /** Optional section id to scroll to once the drawer is open. */
  anchor?: string
}

type MethodologyContextValue = {
  openMethodology: (args: OpenArgs) => void
  closeMethodology: () => void
  isOpen: boolean
}

const MethodologyContext = createContext<MethodologyContextValue | null>(null)

/**
 * Provides a single app-wide methodology drawer instance. Mount once near the
 * router so any descendant can call `useMethodology().openMethodology({...})`.
 * State is deliberately local React state (not Redux) because this UI is
 * transient and page-scoped — matching the pattern used by
 * `HazardScenarioPreferenceControl` rather than the global `MenuDrawer` /
 * `NotificationDrawer`.
 */
export function MethodologyProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; hazardCode: string | null; anchor?: string }>({
    open: false,
    hazardCode: null,
    anchor: undefined,
  })

  const openMethodology = useCallback(({ hazardCode, anchor }: OpenArgs) => {
    setState({ open: true, hazardCode, anchor })
  }, [])
  const closeMethodology = useCallback(() => {
    setState(prev => ({ ...prev, open: false }))
  }, [])

  const value = useMemo(
    () => ({ openMethodology, closeMethodology, isOpen: state.open }),
    [openMethodology, closeMethodology, state.open],
  )

  return (
    <MethodologyContext.Provider value={value}>
      {children}
      <MethodologyDrawer
        open={state.open}
        hazardCode={state.hazardCode}
        anchor={state.anchor}
        onClose={closeMethodology}
      />
    </MethodologyContext.Provider>
  )
}

export function useMethodology(): MethodologyContextValue {
  const ctx = useContext(MethodologyContext)
  if (!ctx) {
    throw new Error('useMethodology must be used inside a <MethodologyProvider>')
  }
  return ctx
}
